import type { DefaultFiltersQuery } from '@api/dto/gql-helpers.generated';
import { useDefaultFiltersQuery } from '@api/dto/gql-helpers.generated';
import { useCallback, useMemo, useRef } from 'react';

type DefaultRailwayFiltersType =
  DefaultFiltersQuery['classifiers']['railwayAdministration']['byEntityId'];
type DefaultPassengerFiltersType =
  DefaultFiltersQuery['classifiers']['passengerCarrier']['byEntityId'];

export type DefaultFiltersType = {
  railwayAdministrationRzd: DefaultRailwayFiltersType | null;
  passengerCarrierFpk: DefaultPassengerFiltersType | null;
  loading: boolean;
  refetch: () => Promise<{
    data?: Pick<DefaultFiltersQuery, 'classifiers'>;
  }>;
};

export const DEFAULT_FILTERS: DefaultFiltersType = {
  railwayAdministrationRzd: null,
  passengerCarrierFpk: null,
  loading: false,
  refetch: async () => ({ data: undefined }),
};

export function useDefaultFilters(
  rzdId: number,
  fpkId: number,
): DefaultFiltersType {
  const rangeStart = useRef(new Date().toISOString().split('T')[0]).current;

  const {
    data: filtersData,
    loading: filtersLoading,
    refetch: refetchFilters,
  } = useDefaultFiltersQuery({
    variables: {
      rzdId: +rzdId,
      fpkId: +fpkId,
      range: { start_at: rangeStart },
    },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const refetch = useCallback(async () => {
    const result = await refetchFilters({
      rzdId: +rzdId,
      fpkId: +fpkId,
      range: { start_at: new Date().toISOString() },
    });
    return {
      data: result.data ? { classifiers: result.data.classifiers } : undefined,
    };
  }, [refetchFilters, rzdId, fpkId]);

  return useMemo(() => {
    const rzdData =
      filtersData?.classifiers.railwayAdministration.byEntityId ?? null;
    const fpkData =
      filtersData?.classifiers.passengerCarrier.byEntityId ?? null;

    return {
      railwayAdministrationRzd: rzdData,
      passengerCarrierFpk: fpkData,
      loading: filtersLoading,
      refetch,
    };
  }, [filtersData, filtersLoading, refetch]);
}
